// Terminal Command Processor
// Handles execution of networking commands

export class CommandProcessor {
  constructor(simulator, device) {
    this.simulator = simulator;
    this.device = device;
    this.continuousPing = null; // Store continuous ping state
  }

  execute(command, onOutput, onAnimate) {
    const parts = command.trim().split(/\s+/);
    const cmd = parts[0].toLowerCase();
    const args = parts.slice(1);

    switch (cmd) {
      case 'help':
        return this.help(onOutput);
      
      case 'ping':
        return this.ping(args, onOutput, onAnimate);
      
      case 'ipconfig':
      case 'ifconfig':
        return this.ipconfig(onOutput);
      
      case 'traceroute':
      case 'tracert':
        return this.traceroute(args[0], onOutput);
      
      case 'arp':
        if (args[0] === '-a') return this.arp(onOutput);
        onOutput('Usage: arp -a', 'error');
        break;
      
      case 'route':
        if (args[0] === 'print') return this.route(onOutput);
        onOutput('Usage: route print', 'error');
        break;
      
      case 'nslookup':
        return this.nslookup(args[0], onOutput);
      
      case 'clear':
      case 'cls':
        this.stopContinuousPing();
        return { clear: true };
      
      case 'hostname':
        return this.hostname(onOutput);
      
      case 'whoami':
        onOutput(`${this.device.name}\\user`, 'success');
        break;
      
      default:
        onOutput(`'${cmd}' is not recognized as an internal or external command.`, 'error');
    }
  }

  help(onOutput) {
    onOutput('Available Commands:', 'info');
    onOutput('', 'info');
    onOutput('  ping <ip-address>       - Test connectivity to a network host (4 packets)', 'info');
    onOutput('  ping -t <ip-address>    - Continuous ping until stopped', 'info');
    onOutput('  traceroute <ip>         - Trace the route packets take to a destination', 'info');
    onOutput('  ipconfig                - Display IP configuration information', 'info');
    onOutput('  arp -a                  - Display the ARP cache table', 'info');
    onOutput('  route print             - Display the routing table', 'info');
    onOutput('  nslookup <domain>       - Query DNS for domain name resolution', 'info');
    onOutput('  hostname                - Display the device hostname', 'info');
    onOutput('  clear                   - Clear the terminal screen', 'info');
    onOutput('  help                    - Display this help message', 'info');
    onOutput('', 'info');
  }

  stopContinuousPing() {
    if (this.continuousPing) {
      clearTimeout(this.continuousPing.timeout);
      clearInterval(this.continuousPing.interval);
      this.continuousPing = null;
    }
  }

  ping(args, onOutput, onAnimate) {
    // Check for -t flag (continuous ping)
    const continuous = args[0] === '-t';
    const targetIp = continuous ? args[1] : args[0];

    if (!targetIp) {
      onOutput('Usage: ping <ip-address> or ping -t <ip-address>', 'error');
      return;
    }

    if (!this.device.powered) {
      onOutput('Device is powered off', 'error');
      return;
    }

    if (!this.device.ip) {
      onOutput('Unable to contact IP driver. Network may not be configured.', 'error');
      return;
    }

    // Stop any existing continuous ping
    this.stopContinuousPing();

    onOutput(`\nPinging ${targetIp} with 32 bytes of data:`, 'info');

    if (continuous) {
      this.doContinuousPing(targetIp, onOutput, onAnimate);
    } else {
      this.doNormalPing(targetIp, onOutput, onAnimate, 4);
    }
  }

  doContinuousPing(targetIp, onOutput, onAnimate) {
    let count = 0;
    const maxCount = 999; // Safety limit

    const sendPing = () => {
      // Check if device is still powered on
      if (!this.device.powered) {
        onOutput('\nRequest timed out.', 'error');
        onOutput('Ping interrupted - device powered off', 'warning');
        this.stopContinuousPing();
        return;
      }

      const result = this.simulator.ping(
        this.device.id,
        targetIp,
        (path) => {
          if (onAnimate) {
            onAnimate(path);
          }
        }
      );

      if (result.error) {
        onOutput(`Request timed out.`, 'error');
      } else if (result.results && result.results.length > 0) {
        const ping = result.results[0];
        onOutput(
          `Reply from ${targetIp}: bytes=32 time=${ping.time}ms TTL=${ping.ttl}`,
          'success'
        );
      }

      count++;
      if (count >= maxCount) {
        this.stopContinuousPing();
        onOutput('\nPing limit reached (999 packets)', 'warning');
        return;
      }

      // Schedule next ping
      this.continuousPing.timeout = setTimeout(sendPing, 1000);
    };

    // Initialize continuous ping state
    this.continuousPing = {
      targetIp,
      count: 0,
      timeout: null,
      interval: null
    };

    // Start pinging
    sendPing();
  }

  doNormalPing(targetIp, onOutput, onAnimate, count = 4) {
    let sentCount = 0;
    let receivedCount = 0;
    const times = [];

    const sendPing = () => {
      if (sentCount >= count) {
        // All pings sent, show statistics
        setTimeout(() => {
          onOutput('', 'info');
          onOutput(`Ping statistics for ${targetIp}:`, 'info');
          onOutput(
            `    Packets: Sent = ${sentCount}, Received = ${receivedCount}, Lost = ${sentCount - receivedCount} (${Math.round((sentCount - receivedCount) / sentCount * 100)}% loss)`,
            'info'
          );
          
          if (times.length > 0) {
            onOutput('Approximate round trip times in milli-seconds:', 'info');
            onOutput(
              `    Minimum = ${Math.min(...times)}ms, Maximum = ${Math.max(...times)}ms, Average = ${Math.round(times.reduce((a, b) => a + b) / times.length)}ms`,
              'info'
            );
          }
        }, 100);
        return;
      }

      // Check if device is still powered on
      if (!this.device.powered) {
        onOutput('Request timed out.', 'error');
        onOutput('Ping interrupted - device powered off', 'warning');
        return;
      }

      const result = this.simulator.ping(
        this.device.id,
        targetIp,
        (path) => {
          if (onAnimate) {
            onAnimate(path);
          }
        }
      );

      sentCount++;

      if (result.error) {
        onOutput(`Request timed out.`, 'error');
      } else if (result.results && result.results.length > 0) {
        const ping = result.results[0];
        receivedCount++;
        times.push(ping.time);
        onOutput(
          `Reply from ${targetIp}: bytes=32 time=${ping.time}ms TTL=${ping.ttl}`,
          'success'
        );
      }

      // Schedule next ping after 1 second delay
      setTimeout(sendPing, 1000);
    };

    // Start first ping
    sendPing();
  }

  ipconfig(onOutput) {
    onOutput('', 'info');
    onOutput('Windows IP Configuration', 'info');
    onOutput('', 'info');
    onOutput('Ethernet adapter Local Area Connection:', 'info');
    onOutput('', 'info');
    
    if (!this.device.ip) {
      onOutput('   Media State . . . . . . . . . . . : Media disconnected', 'error');
    } else {
      onOutput(`   IPv4 Address. . . . . . . . . . . : ${this.device.ip}`, 'success');
      onOutput(`   Subnet Mask . . . . . . . . . . . : ${this.device.subnet}`, 'success');
      
      if (this.device.gateway) {
        onOutput(`   Default Gateway . . . . . . . . . : ${this.device.gateway}`, 'success');
      }
      
      if (this.device.dns) {
        onOutput(`   DNS Servers . . . . . . . . . . . : ${this.device.dns}`, 'success');
      }
      
      onOutput(`   Physical Address. . . . . . . . . : ${this.device.mac}`, 'success');
      onOutput(`   DHCP Enabled. . . . . . . . . . . : ${this.device.dhcpEnabled ? 'Yes' : 'No'}`, 'success');
    }
    
    onOutput('', 'info');
  }

  traceroute(targetIp, onOutput) {
    if (!targetIp) {
      onOutput('Usage: traceroute <ip-address>', 'error');
      return;
    }

    if (!this.device.ip) {
      onOutput('Unable to contact IP driver.', 'error');
      return;
    }

    onOutput(`\nTracing route to ${targetIp} over a maximum of 30 hops:\n`, 'info');

    const result = this.simulator.traceroute(this.device.id, targetIp);

    if (result.error) {
      onOutput(`  ${result.error}`, 'error');
      return;
    }

    setTimeout(() => {
      result.hops.forEach(hop => {
        const time1 = hop.latency;
        const time2 = hop.latency + Math.floor(Math.random() * 2);
        const time3 = hop.latency + Math.floor(Math.random() * 2);
        
        onOutput(
          `  ${hop.hop}    ${time1} ms    ${time2} ms    ${time3} ms    ${hop.ip} [${hop.name}]`,
          'success'
        );
      });

      onOutput('\nTrace complete.', 'info');
    }, 300);
  }

  arp(onOutput) {
    onOutput('', 'info');
    onOutput(`Interface: ${this.device.ip} --- 0x2`, 'info');
    onOutput('  Internet Address      Physical Address      Type', 'info');
    
    const arpTable = this.simulator.getArpTable(this.device.id);
    
    if (arpTable.length === 0) {
      onOutput('  (No ARP entries)', 'info');
    } else {
      arpTable.forEach(entry => {
        const ip = entry.ip.padEnd(20);
        const mac = entry.mac.padEnd(20);
        onOutput(`  ${ip}  ${mac}  ${entry.type}`, 'success');
      });
    }
    
    onOutput('', 'info');
  }

  route(onOutput) {
    onOutput('', 'info');
    onOutput('IPv4 Route Table', 'info');
    onOutput('===========================================================================', 'info');
    onOutput('Active Routes:', 'info');
    onOutput('Network Destination        Netmask          Gateway       Interface  Metric', 'info');
    
    const routingTable = this.simulator.getRoutingTable(this.device.id);
    
    routingTable.forEach(route => {
      const dest = route.destination.padEnd(24);
      const mask = route.netmask.padEnd(16);
      const gw = route.gateway.padEnd(13);
      const iface = route.interface.padEnd(10);
      
      onOutput(`${dest} ${mask} ${gw} ${iface} ${route.metric}`, 'success');
    });
    
    onOutput('===========================================================================', 'info');
    onOutput('', 'info');
  }

  nslookup(domain, onOutput) {
    if (!domain) {
      onOutput('Usage: nslookup <domain>', 'error');
      return;
    }

    const dnsServer = this.device.dns || '8.8.8.8';
    
    onOutput('', 'info');
    onOutput(`Server:  dns.google.com`, 'info');
    onOutput(`Address:  ${dnsServer}`, 'info');
    onOutput('', 'info');
    
    // Simulate DNS lookup
    setTimeout(() => {
      onOutput('Non-authoritative answer:', 'info');
      onOutput(`Name:    ${domain}`, 'success');
      
      // Generate a fake IP for the domain
      const fakeIp = `${Math.floor(Math.random() * 200 + 1)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`;
      onOutput(`Address:  ${fakeIp}`, 'success');
      onOutput('', 'info');
    }, 400);
  }

  hostname(onOutput) {
    onOutput(this.device.name, 'success');
  }
}
